/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { Comment } from 'antd';
import classNames from 'classnames/bind';
import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { AttachmentsAPI } from 'api/attachments';
import { Comment as Comment$ } from 'api/document/types/document';
import {
  CommentHead,
  Content,
  ReplyComment,
  TextFromDoc,
} from 'components/molecules/comments';
import styles from 'components/organisms/document/parts/doc-discussions/doc-discussions.module.css';
import { DiscussionProps as IDiscussionProps } from 'components/organisms/document/parts/doc-discussions/types';
import { useGetDiscussionsForAttachment } from 'queries/attachments';

export type DiscussionProps = Omit<IDiscussionProps, 'docId' | 'editor'> & {
  attachmentId: number;
  onReply: (body: { content: string; isPrivate: boolean }) => void;
  onDeleteThread: () => void;
  onHover?: () => void;
  onResolve?: () => void;
  onEditComment: (comment: Comment$, content: string) => void;
};

const StyledComment = styled(Comment)<{ isPrivate: boolean }>`
  background-color: ${({ isPrivate }) => isPrivate && 'var(--private-02)'};
  .ant-comment-content-author-name {
    width: 100%;
  }
`;

export const Discussion: FC<DiscussionProps> = ({
  projectId,
  attachmentId,
  discussion,
  isActiveDiscussion,
  editorCommentMap,
  onDiscussionClick,
  onReply,
  onDeleteThread,
  onHover,
  onResolve,
  onEditComment,
}) => {
  const [editingComment, setEditingComment] = useState(0);
  const { refetch } = useGetDiscussionsForAttachment(attachmentId);
  const renderHiddenCommentsInfo = (value) =>
    `${value} comment${value > 1 ? 's' : ''}`;

  const enableEditComment = (comment) => {
    setEditingComment(comment.commentId);
  };

  const deleteCommment = (_comment) => {
    AttachmentsAPI.deleteComment(attachmentId, _comment.commentId).then(() =>
      refetch(),
    );
  };

  const onPerformThreadDelete = () => {
    onDeleteThread();
  };

  const onPerformThreadResolve = () => {
    AttachmentsAPI.resolveDocumentThread(
      attachmentId,
      discussion.commentThread.threadId,
      true,
    ).then(() => refetch());
    onResolve?.();
  };

  const cx = classNames.bind(styles);
  /* eslint-disable @typescript-eslint/naming-convention */
  const generateDiscussionClasses = (index, discussionLength) =>
    cx('comment', {
      comment__intermediate: index !== 0 && index !== discussionLength - 1,
      comment__intermediate__info: index === discussionLength - 2,
      reply: index !== 0,
    });
  /* eslint-enable @typescript-eslint/naming-convention */

  return (
    <div
      className={cx(
        'discussion',
        { active: isActiveDiscussion },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        { discussion__private: discussion.comments[0]?.private },
      )}
      onClick={onDiscussionClick}
      id={`commentThread-rl-comment-${discussion.commentThread.threadId}`}
    >
      {editorCommentMap[discussion.commentThread.threadId]?.content ? (
        <TextFromDoc
          linkedId={discussion.commentThread.threadId.toString()}
          content={editorCommentMap[discussion.commentThread.threadId]?.content}
          contentType={discussion.comments[0]?.private ? 'private' : 'public'}
        />
      ) : null}
      {discussion.comments.map((comment, index) => (
        // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
        <div key={comment.commentId} onMouseOver={onHover}>
          <StyledComment
            className={generateDiscussionClasses(
              index,
              discussion.comments.length,
            )}
            isPrivate={comment.private}
            author={
              <CommentHead
                shouldHide={editingComment === comment.commentId}
                isThreadComment={index === 0}
                actionClass={styles.actions}
                actionClassResolved={styles.actionClassResolved}
                comment={comment}
                authorClass={styles.author}
                onPerformDelete={deleteCommment}
                onPerformEdit={enableEditComment}
                onPerformThreadDelete={onPerformThreadDelete}
                onPerformThreadResolve={onPerformThreadResolve}
              />
            }
            content={
              <Content
                edit={editingComment === comment.commentId}
                onEditFormClose={() => setEditingComment(0)}
                comment={comment}
                projectId={projectId}
                isPrivateDiscussion={discussion.comments[0]?.private}
                isActiveDiscussion={isActiveDiscussion}
                onEdit={(content) => onEditComment(comment, content)}
                isParent={index === 0}
              />
            }
          />
          {index === discussion.comments.length - 2 &&
          discussion.comments.length > 2 ? (
            <div className={styles.hidden__comments_info}>
              {renderHiddenCommentsInfo(discussion.comments.length - 2)}
            </div>
          ) : null}
        </div>
      ))}
      <div className={!isActiveDiscussion ? 'hide' : ''}>
        <ReplyComment
          onReply={onReply}
          fromPrivateDiscussion={discussion.comments[0]?.private}
          isPrivateComment={discussion.comments[0]?.private}
          projectId={projectId}
        />
      </div>
    </div>
  );
};
